import { useEffect, useMemo, useRef, useState } from 'react';
import { get, getPhotosBaseUrl } from '../../services/api';
import { useUI } from '../../UIContext';
import { handleErr } from '../../utils/functions';
import telegramImage from '../../imgs/telegram.png';
import whatsappImage from '../../imgs/whatsapp.png';
import './community.css';

function Community() {
    const { setLoading, setMessage } = useUI();
    const mobileBreakpoint = 799;
    const [pageLoaded, setPageLoaded] = useState(false);
    const [instructors, setInstructors] = useState([]);
    const [whatsappGroupLink, setWhatsappGroupLink] = useState('');
    const [telegramGroupLink, setTelegramGroupLink] = useState('');
    const [instructorsAreaHeight, setInstructorsAreaHeight] = useState(undefined);
    const [isMobileLayout, setIsMobileLayout] = useState(() => window.innerWidth <= mobileBreakpoint);
    const pageRef = useRef(null);
    const linksRef = useRef(null);
    const teachers = useMemo(() => {
        return instructors.map((instructor) => ({
            id: instructor.id,
            fullName: getInstructorFullName(instructor),
            photo: getInstructorPhoto(instructor),
            description: getInstructorDescription(instructor)
        }));
    }, [instructors]);
    const featuredTeacher = teachers[0];
    const otherTeachers = teachers.slice(1);

    useEffect(() => {
        async function loadCommunityData() {
            setLoading(true);

            await Promise.all([
                get('/instructor', true, setMessage,
                    (response) => {
                        setInstructors(parseInstructorsResponse(response.data));
                    },
                    (err) => {
                        handleErr(setMessage, `Nao foi possivel carregar os professores: ${err}`);
                    },
                    () => {}
                ),
                get('/config', true, setMessage,
                    (response) => {
                        const { whatsapp, telegram } = parseCommunityLinks(response.data);
                        setWhatsappGroupLink(whatsapp);
                        setTelegramGroupLink(telegram);
                    },
                    (err) => {
                        handleErr(setMessage, `Nao foi possivel carregar os links da comunidade: ${err}`);
                    },
                    () => {}
                )
            ]);

            setLoading(false);
            setPageLoaded(true);
        }

        loadCommunityData();
    }, [setLoading, setMessage]);

    useEffect(() => {
        function updateMobileLayoutState() {
            setIsMobileLayout(window.innerWidth <= mobileBreakpoint);
        }

        updateMobileLayoutState();
        window.addEventListener('resize', updateMobileLayoutState);

        return () => {
            window.removeEventListener('resize', updateMobileLayoutState);
        };
    }, [mobileBreakpoint]);

    useEffect(() => {
        function updateInstructorsAreaHeight() {
            if (isMobileLayout) {
                setInstructorsAreaHeight(undefined);
                return;
            }

            if (!pageRef.current || !linksRef.current)
                return;

            const pageTop = pageRef.current.getBoundingClientRect().top;
            const linksHeight = linksRef.current.getBoundingClientRect().height;
            const viewportHeight = window.innerHeight;
            const verticalGap = 24;
            const minHeight = 180;
            const availableHeight = viewportHeight - pageTop - linksHeight - verticalGap;

            setInstructorsAreaHeight(Math.max(minHeight, Math.floor(availableHeight)));
        }

        updateInstructorsAreaHeight();
        window.addEventListener('resize', updateInstructorsAreaHeight);

        return () => {
            window.removeEventListener('resize', updateInstructorsAreaHeight);
        };
    }, [isMobileLayout, teachers.length]);

    if (!pageLoaded)
        return null;

    return (
        <main className='community-page' ref={pageRef}>
            <div className='glass-bg-full-page'></div>

            <section className='community-instructors-section'>
                {teachers.length === 0 ? (
                    <p className='community-empty-text'>Nenhum professor encontrado no momento.</p>
                ) : (
                    <div
                        className='community-instructors-scroll-area'
                        style={{ maxHeight: instructorsAreaHeight ? `${instructorsAreaHeight}px` : undefined }}>
                        {featuredTeacher && (
                            <article
                                key={featuredTeacher.id || featuredTeacher.fullName}
                                className='community-teacher-card community-teacher-card-featured container-white'
                                aria-label={featuredTeacher.fullName}>
                                <div
                                    className='community-teacher-image'
                                    style={{ backgroundImage: featuredTeacher.photo ? `url(${featuredTeacher.photo})` : 'none' }}>
                                    {!featuredTeacher.photo && <span>Sem imagem</span>}
                                </div>

                                <div className='community-teacher-content'>
                                    <h3>{featuredTeacher.fullName}</h3>
                                    {featuredTeacher.description && (
                                        <p className='community-teacher-description'>{featuredTeacher.description}</p>
                                    )}
                                </div>
                            </article>
                        )}

                        {otherTeachers.length > 0 && (
                            <div className='community-instructors-grid'>
                                {otherTeachers.map((teacher) => (
                                    <article
                                        key={teacher.id || teacher.fullName}
                                        className='community-teacher-card container-white'
                                        aria-label={teacher.fullName}>
                                        <div
                                            className='community-teacher-image'
                                            style={{ backgroundImage: teacher.photo ? `url(${teacher.photo})` : 'none' }}>
                                            {!teacher.photo && <span>Sem imagem</span>}
                                        </div>

                                        <div className='community-teacher-content'>
                                            <h3>{teacher.fullName}</h3>
                                            {teacher.description && (
                                                <p className='community-teacher-description'>{teacher.description}</p>
                                            )}
                                        </div>
                                    </article>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </section>

            <section className='community-links-grid' ref={linksRef}>
                <article className='community-group-card container-white'>
                    <div className='community-group-image-wrapper'>
                        <img src={whatsappImage} alt='WhatsApp' className='community-group-image' />
                    </div>

                    <div className='community-group-content'>
                        <div>
                            <h2>Grupo no WhatsApp</h2>
                            <p>Entre no grupo para trocar experiencias e acompanhar novidades com a comunidade.</p>
                        </div>

                        <a
                            className={`blue-button community-link-button ${!whatsappGroupLink ? 'disabled-button' : ''}`}
                            href={whatsappGroupLink || undefined}
                            target='_blank'
                            rel='noreferrer'
                            aria-disabled={!whatsappGroupLink}
                            onClick={(e) => {
                                if (!whatsappGroupLink)
                                    e.preventDefault();
                            }}>
                            Acessar grupo
                        </a>
                    </div>
                </article>

                <article className='community-group-card container-white'>
                    <div className='community-group-image-wrapper'>
                        <img src={telegramImage} alt='Telegram' className='community-group-image' />
                    </div>

                    <div className='community-group-content'>
                        <div>
                            <h2>Canal no Telegram</h2>
                            <p>Acesse o canal para receber atualizacoes importantes e conteudos compartilhados.</p>
                        </div>

                        <a
                            className={`blue-button community-link-button ${!telegramGroupLink ? 'disabled-button' : ''}`}
                            href={telegramGroupLink || undefined}
                            target='_blank'
                            rel='noreferrer'
                            aria-disabled={!telegramGroupLink}
                            onClick={(e) => {
                                if (!telegramGroupLink)
                                    e.preventDefault();
                            }}>
                            Acessar canal
                        </a>
                    </div>
                </article>
            </section>
        </main>
    );
}

function parseInstructorsResponse(data) {
    if (Array.isArray(data))
        return data;

    if (Array.isArray(data?.instructors))
        return data.instructors;

    if (Array.isArray(data?.items))
        return data.items;

    return [];
}

function parseCommunityLinks(data) {
    const configs = Array.isArray(data)
        ? data
        : Array.isArray(data?.configs)
            ? data.configs
            : [];

    const getByType = (type) => {
        const config = configs.find((item) => item?.type === type);
        return typeof config?.value === 'string' ? config.value : '';
    };

    return {
        whatsapp: getByType('WhatsappGroup'),
        telegram: getByType('TelegramGroup')
    };
}

function getInstructorFullName(instructor) {
    const fullName = [instructor?.name, instructor?.lastName]
        .filter((part) => !!part)
        .join(' ')
        .trim();

    return fullName || 'Professor';
}

function getInstructorPhoto(instructor) {
    const photoName = getImageName(instructor);

    if (!photoName)
        return '';

    return `${getPhotosBaseUrl()}/instructors/${photoName}`;
}

function getImageName(instructor) {
    const photoPath = instructor?.photoPath;

    if (!photoPath || typeof photoPath !== 'string')
        return undefined;

    const imagePathSplitted = photoPath.split('/');
    return imagePathSplitted[imagePathSplitted.length - 1];
}

function getInstructorDescription(instructor) {
    const description = instructor?.description;

    if (typeof description !== 'string')
        return '';

    return description.trim();
}

export default Community;